import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import BorderColorIcon from '@mui/icons-material/BorderColorOutlined';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
// import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { Box, Button, Checkbox, Chip, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
// import clsx from 'clsx';
import * as React from 'react';
import { useDrag, useDrop } from 'react-dnd';

import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { guestsSeeText, renderingTypeLabel } from '../pretrip/utils';
import { ContextStore } from '../store/contextStore';
import { EMDASH, toTitleCase } from '../utils';
import { validateItem } from './utils';
import useConfirmDialog from '../components/useConfirmDialog';

const DND_ITEM = 'pretrip_item';

// Tooltip text for the "Visible to" chips; unknown types fall back to the raw value.
const VISIBILITY_TYPE_LABELS = {
    allusers: 'All users',
    travelers: 'Travelers',
    access_group: 'Access group',
    group: 'Role',
    role: 'Role',
    user: 'User',
};

// Width of the up/down arrows group. Exported so the list header can align its
// "Select all" label with the left edge of the first arrow button.
export const ARROWS_GROUP_WIDTH = { xs: '4.5rem', sm: '5.5rem' };

const useStyles = makeStyles((theme) => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            width: '90%',
            padding: '1rem',
        },
    },
    itemRoot: {
        margin: 0,
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    dragHandle: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    arrowButton: {
        padding: '0.5rem',
        border: `solid 1px ${grey[400]}`,
        borderRadius: '5px',
        cursor: 'pointer',
        background: 'transparent',
        color: theme.palette.primary.main,
        [theme.breakpoints.down('sm')]: {
            padding: '0.25rem',
        },
    },
}));

const getUsersAndGroups = (pti, trip) => {
    const L = [];
    if (pti.all_users) {
        L.push({ id: 0, name: 'All Users', type: 'allusers' });
    }
    if (pti.all_travelers) {
        L.push({ id: 0, name: 'All Travelers', type: 'travelers' });
    }
    (pti.roles || []).forEach((r) => L.push({ id: r.id, name: r.name, type: 'group' }));
    (pti.users || []).forEach((r) => L.push({ id: r.id, name: r.name, role: r.role, last_name: r.last_name, first_name: r.first_name, type: 'user' }));
    (pti.agroups || []).forEach((r) => L.push({ id: r.id, name: r.name, codename: r.codename, type: 'access_group' }));
    return L;
};

const PreTripItem = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const classes = useStyles();
    const pti = props.item;
    const usersAndGroups = getUsersAndGroups(pti, props.trip);
    const { tourOperator } = React.useContext(ContextStore);
    const dndref = React.useRef(null);
    const [isSmallScreen, setIsSmallScreen] = React.useState(false);
    const preTripItemVisibilityEnabled = !!tourOperator?.config?.enable_pre_trip_items_visibility;

    React.useEffect(() => {
        if (window.innerWidth < 600) {
            setIsSmallScreen(true);
        }
    }, []);

    const onUserAndGroupChange = (opt, selected) => {
        let nv = [opt, ...selected];

        if (props.trip.is_bespoke) {
            props.onChange &&
                props.onChange(
                    {
                        // ...pti,
                        all_users: nv.filter((ug) => ug.type === 'allusers').length === 1,
                        all_travelers: nv.filter((ug) => ug.type === 'travelers').length === 1,
                        roles: nv.filter((ug) => ug.type === 'group'),
                        users: nv.filter((ug) => ug.type === 'user'),
                        agroups: nv.filter((ug) => ug.type === 'access_group'),
                        _changed: true,
                        _changed_user: true,
                    },
                    props.idx
                );
        } else {
            props.onChange &&
                props.onChange(
                    {
                        // ...pti,
                        all_users: nv.filter((ug) => ug.type === 'allusers').length === 1,
                        all_travelers: nv.filter((ug) => ug.type === 'travelers').length === 1,
                        roles: nv.filter((ug) => ug.type === 'group'),
                        agroups: nv.filter((ug) => ug.type === 'access_group'),
                        // users: nv.filter(ug => (ug.type === "user")),
                        _changed: true,
                        _changed_user: true,
                    },
                    props.idx
                );
        }
    };
    const onItemChange = (ev, nv) => {
        console.log('item change', ev, nv);
        const update = {
            ...pti,
            item: nv,
            _changed: true,
            _changed_item: true,
        };
        props.onChange && props.onChange(update, props.idx);
        const { canSave, errors } = validateItem(update);
        props.onValidate && props.onValidate(`pre_trip_items[${props.idx}]`, canSave, errors);
    };
    const onDelete = (ev) => {
        ev.preventDefault();
        confirmDelete({ body: `Are you sure you want to delete "${props.item.item.name}"?` }).then((ok) => {
            if (ok) {
                props.onDelete && props.onDelete(props.item, props.idx);
            }
        });
        return false;
    };

    // eslint-disable-next-line no-unused-vars
    const [{ handlerId }, drop] = useDrop({
        accept: DND_ITEM,
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover: (item, monitor) => {
            if (!dndref.current) {
                return;
            }
            const dragIndex = item.idx;
            const hoverIndex = props.idx;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = dndref.current?.getBoundingClientRect();
            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            // Determine mouse position
            const clientOffset = monitor.getClientOffset();
            // Get pixels to the top
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%
            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            if (props.swapItems) {
                props.swapItems(dragIndex, hoverIndex);
                item.idx = hoverIndex;
            }
        },
        drop: (item, monitor) => {
            props.saveOrder && props.saveOrder();
        },
    });

    // eslint-disable-next-line no-unused-vars
    const [collected, drag, dragPreview] = useDrag({
        type: DND_ITEM,
        item: () => {
            return { id: pti.id, idx: props.idx };
        },
        collect: (monitor, a) => {
            return {
                isDragging: monitor.isDragging(),
            };
        },
        end: (item, monitor) => {
            props.saveOrder && props.saveOrder();
        },
    });
    drag(drop(dndref));

    // eslint-disable-next-line no-unused-vars
    const { canSave, errors } = validateItem(pti);
    // <div ref={drop} className={clsx(classes.itemRoot, collected.isDragging && classes.dragPreview)}>
    return (
        <>
            {confirmDeleteDialog}
            <Grid container direction="row">
                {/* Half of a 1/12 column. Width is set via sx only — a breakpoint prop here would win over sx inside its media query. */}
                {props.selectable && (
                    <Grid item container alignItems="center" justifyContent="center" sx={{ flexGrow: 0, flexBasis: '4.166667%', maxWidth: '4.166667%' }}>
                        <Checkbox
                            size="small"
                            sx={{ padding: '0.25rem' }}
                            checked={!!props.selected}
                            onChange={(ev) => props.onSelectChange && props.onSelectChange(props.idx, ev.target.checked)}
                            inputProps={{ 'aria-label': 'Select trip item' }}
                        />
                    </Grid>
                )}
                <Grid item xs={3} sm={1} container alignItems="center" justifyContent="center">
                    {!props.disabled && <Box>
                        <Box sx={{ display: 'flex', gap: '0.25rem', width: ARROWS_GROUP_WIDTH }}>
                            <Tooltip title="Move up">
                                <button className={classes.arrowButton} style={{ color: props.idx === 0 && grey[400], cursor: props.idx === 0 && 'default' }} onClick={() => props.swapItems(props.idx, props.idx - 1)}>
                                    <ArrowUpwardIcon />
                                </button>
                            </Tooltip>
                            <Tooltip title="Move down">
                                <button className={classes.arrowButton} style={{ color: props.idx === props.trip.pre_trip_items.length - 1 && grey[400], cursor: props.idx === props.trip.pre_trip_items.length - 1 && 'default' }} onClick={() => props.swapItems(props.idx, props.idx + 1)}>
                                    <ArrowDownwardIcon />
                                </button>
                            </Tooltip>
                        </Box>
                    </Box>}
                </Grid>
                {/* Absorbs the space freed by the narrow checkbox column (plus any rounding slack, so nothing wraps). */}
                <Grid item container alignItems="center" sx={{ flexGrow: 1, flexBasis: 0, maxWidth: 'none' }}>
                    {/* CP-6187 (Rea): the Friendly Name the app shows in place of the Name, to compare with the app.
                        Read-only: the item is shared by every tour that lists it, so it is edited on its own page. */}
                    <RemoteAutocomplete disabled={props.disabled} textFieldProps={{ ...(errors.item || { helperText: guestsSeeText(pti.item) }) }} resource="pre_trip_item" urlSuffix={null} label="Trip Item" variant="filled" size="small" fullWidth onChange={onItemChange} value={pti.item} />
                </Grid>
                {isSmallScreen && (
                    <>
                        <Grid item xs={3} sm={false} container alignItems="center" justifyContent="flex-end">
                            <DeleteIconButton disabled={props.disabled} onClick={onDelete} />

                            <Tooltip title="Edit trip item in new tab">
                                <span style={{ display: 'inline-flex' }}>
                                    <IconButton color={pti && pti.item && pti.item.id ? 'primary' : 'inherit'} href={`/pretrip/items/${pti && pti.item ? pti.item.id : 0}`} disabled={!(pti && pti.item && pti.item.id) || props.disabled} target="_blank">
                                        <BorderColorIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    </>
                )}

                <Grid item xs={12} sm={3} container alignItems="center" p={1} justifyContent={isSmallScreen ? 'flex-start' : 'center'}>
                    <Typography textAlign="center" variant="body2">
                        {pti.item ? `${toTitleCase(pti.item.item_type.name)} (${renderingTypeLabel(pti.item.item_type.rendering_type)})` : EMDASH}{' '}
                    </Typography>
                </Grid>
                <Grid item xs={isSmallScreen ? 11 : 12} sm={3} container alignItems="center" justifyContent="flex-end">
                    {preTripItemVisibilityEnabled && <>
                        <RemoteAutocomplete
                            multiple
                            resource="users_and_groups"
                            urlSuffix={null}
                            label="Visible to"
                            variant="filled"
                            size="small"
                            fullWidth
                            onChange={onUserAndGroupChange}
                            apiParams={props.apiParams}
                            value={usersAndGroups}
                            getOptionSelected={(opt, val) => {
                                return opt.type === val.type && (isNaN(+opt.id) || isNaN(+val.id) || +opt.id === +val.id);
                            }}
                            renderTags={(value, getTagProps) => value.map((option, index) => {
                                let Icon = <PersonIcon/>;
                                if (option.type === 'allusers' || option.type === 'travelers') {
                                    Icon = <GroupsIcon/>;
                                } else if (option.type === 'access_group') {
                                    Icon = <PeopleIcon/>;
                                } else if (option.type === 'group') {
                                    Icon = <PeopleIcon color="secondary"/>;
                                }
                                return <Tooltip key={index} title={VISIBILITY_TYPE_LABELS[option.type] || option.type}>
                                    <Chip
                                        icon={Icon}
                                        variant="outlined"
                                        label={option.name}
                                        // size="small"
                                        {...getTagProps({ index })}
                                    />
                                </Tooltip>
                            })}
                            filterOptions={(options, state) => {
                                const uag = getUsersAndGroups(pti, props.trip);
                                const uagmap = {};
                                uag.forEach(x => {
                                    if (!uagmap[x.type]) {
                                        uagmap[x.type] = {}
                                    }
                                    uagmap[x.type][x.id] = x;
                                });
                                const newOptions = options.filter(opt => {
                                    if (!uagmap[opt.type]) { return true; }
                                    if (!!uagmap[opt.type][opt.id]) { return false; }
                                    return true;
                                });
                                const filterSelectedOptions = newOptions.filter(option => !uag.map(({name})=>name.toLowerCase()).includes(option.name.toLowerCase()));
                                return filterSelectedOptions;
                            }}
                            renderOption={(mui, opt, params, data) => {
                                if (!props.trip.is_bespoke && opt.type === 'user') {
                                    return;
                                }
                                if (opt.type === 'allusers' || opt.type === 'travelers') {
                                    return (
                                        <Box sx={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', 'alignItems': 'center' }} p={1} onClick={() => onUserAndGroupChange(opt, data.value)}>
                                            <GroupsIcon/>&nbsp;<Typography> {toTitleCase(opt.name)}</Typography>
                                        </Box>
                                    );
                                } else if (opt.type === 'access_group') {
                                    return (
                                        <Box sx={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', 'alignItems': 'center' }} p={1} onClick={() => onUserAndGroupChange(opt, data.value)}>
                                            <PeopleIcon/>&nbsp;<Typography>Access group: {opt.name} {opt.codename ? ` (${opt.codename})` : null}</Typography>
                                        </Box>
                                    );
                                } else if (opt.type === 'group') {
                                    return (
                                        <Box sx={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', 'alignItems': 'center' }} p={1} onClick={() => onUserAndGroupChange(opt, data.value)}>
                                            <PeopleIcon color="secondary"/>&nbsp;<Typography>Role (legacy): {opt.name}</Typography>
                                        </Box>
                                    );
                                } else if (opt.type === 'user') {
                                    if (opt.role) {
                                        return (
                                            <Box sx={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', 'alignItems': 'center' }} p={1} onClick={() => onUserAndGroupChange(opt, data.value)}>
                                                <PersonIcon/>&nbsp;<Typography>
                                                    {toTitleCase(opt.role.name)}: {opt.name}
                                                </Typography>
                                            </Box>
                                        );
                                    } else {
                                        return (
                                            <Box sx={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', 'alignItems': 'center' }} p={1} onClick={() => onUserAndGroupChange(opt, data.value)}>
                                                <Typography>Traveler: {opt.name}</Typography>
                                            </Box>
                                        );
                                    }
                                }
                            }}
                        />
                    </>}
                </Grid>

                {!isSmallScreen && (
                    <Grid item xs={false} sm={1} container alignItems="center" justifyContent="flex-end">
                        <Grid item xs={6} container alignItems="center" justifyContent="flex-end">
                            <Tooltip title="Edit trip item in new tab">
                                <span style={{ display: 'inline-flex' }}>
                                    <Button color={pti && pti.item && pti.item.id ? 'primary' : 'inherit'} href={`/pretrip/items/${pti && pti.item ? pti.item.id : 0}`} disabled={!(pti && pti.item && pti.item.id) || props.disabled} target="_blank">
                                        <EditIcon fontSize="small" />
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={6}>
                            <DeleteIconButton disabled={props.disabled} onClick={onDelete} />
                        </Grid>
                    </Grid>
                )}
            </Grid>

            {!props.isLast ? (
                <>
                    <br />
                    <Divider className={classes.divider} /> <br />{' '}
                </>
            ) : null}
        </>
    );
};

export default PreTripItem;
